import { t } from "@lingui/macro";
import {
  type BulkEditTaskInput,
  BulkTasksDeleteDocument,
  type BulkTasksDeleteMutation,
  type BulkTasksDeleteMutationVariables,
  BulkTasksEditDocument,
  type BulkTasksEditMutation,
  type BulkTasksEditMutationVariables,
  BulkTasksOptionsDocument,
  type BulkTasksOptionsQuery,
  type BulkTasksOptionsQueryVariables,
  PrepareTaskDataForBulkActionsDocument,
  type PrepareTaskDataForBulkActionsQuery,
  type PrepareTaskDataForBulkActionsQueryVariables,
  type Scalars,
  StarsStarableTypeEnum,
  type TaskDescriptionInput,
  type TimeTrackingWorkType,
  UsersSelectOptionsDocument,
  type UsersSelectOptionsQuery,
  type UsersSelectOptionsQueryVariables,
} from "@src/__generated__/graphql";
import type { IOption } from "@src/components/ui-kit/Select";
import {
  EMPTY_EDITOR_CONTENT,
  EMPTY_EDITOR_LENGTH,
  type TextEditorRef,
} from "@src/components/ui-kit/TextEditor/TextEditor";
import { BulkActionsStore } from "@src/components/widgets/bulk-actions/store";
import { PositionMappingStore } from "@src/components/widgets/Modals/ModalTask/position-mapping-store";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import { toApiDate } from "@src/utils/dates";
import { ONE_HOUR_IN_SECONDS } from "@src/utils/formatters";
import { required } from "@src/utils/forms/validators";
import mapToOptions from "@src/utils/map-to-options";
import type { Content } from "@tiptap/core";
import { FieldState, FormState } from "formstate";
import { action, computed, makeObservable, observable } from "mobx";
import { createRef } from "react";

type ActionType = "edit" | "duplicate" | "move" | "delete" | null;
type TaskPositionForm = FormState<{
  user_id: FieldState<Scalars["ID"]["input"]>;
  planned_time: FieldState<string>;
  time_tracking_work_type_id: FieldState<Scalars["ID"]["input"]>;
}>;

export class TasksBulkActionsStore extends BulkActionsStore {
  @observable
  descriptionInputRef = createRef<TextEditorRef>();

  @observable.ref
  actionType: ActionType = null;

  @observable.ref
  taskStatusOptions: IOption[] = [];

  @observable.ref
  workTypeOptions: IOption[] = [];

  @observable.ref
  userOptions: IOption[] = [];

  @observable.ref
  unavailableWorkTypes: Pick<TimeTrackingWorkType, "id" | "title">[] = [];

  positionMappingStore = new PositionMappingStore();

  @observable.ref
  mentionedUserIds: TaskDescriptionInput["mentioned_user_ids"] = [];

  @observable.ref
  mentionedTeamIds: TaskDescriptionInput["mentioned_team_ids"] = [];

  @observable.ref
  mentionedAssignees: TaskDescriptionInput["mentioned_assignees"] = false;

  private usersFetcher = new FetchHelper<
    UsersSelectOptionsQuery,
    UsersSelectOptionsQueryVariables
  >(UsersSelectOptionsDocument);
  private optionsFetcher = new FetchHelper<
    BulkTasksOptionsQuery,
    BulkTasksOptionsQueryVariables
  >(BulkTasksOptionsDocument);
  private prepareTaskDataFetcher = new FetchHelper<
    PrepareTaskDataForBulkActionsQuery,
    PrepareTaskDataForBulkActionsQueryVariables
  >(PrepareTaskDataForBulkActionsDocument);
  private editMutator = new MutationHelper<
    BulkTasksEditMutation,
    BulkTasksEditMutationVariables
  >(BulkTasksEditDocument);
  private deleteMutator = new MutationHelper<
    BulkTasksDeleteMutation,
    BulkTasksDeleteMutationVariables
  >(BulkTasksDeleteDocument);

  requiredIfProjectIsSelected = (
    $: string | number | null | object | undefined,
  ) => {
    if (this.form.$.project_id.value) {
      return required($ ?? undefined);
    }

    return undefined;
  };

  form = new FormState({
    task_status_id: new FieldState<BulkEditTaskInput["task_status_id"]>(
      undefined,
    ),
    task_priority_id: new FieldState<BulkEditTaskInput["task_priority_id"]>(
      undefined,
    ),
    billable: new FieldState<"true" | "false" | undefined>(undefined),
    project_id: new FieldState<string | undefined>(undefined),
    our_work_budget_item_id: new FieldState<
      BulkEditTaskInput["our_work_budget_item_id"]
    >(undefined).validators(this.requiredIfProjectIsSelected),
    from: new FieldState<Date | undefined>(undefined),
    to: new FieldState<Date | undefined>(undefined),
    deadline: new FieldState<Date | undefined>(undefined),
    parent_task_id: new FieldState<string | undefined>(undefined),
    name: new FieldState<BulkEditTaskInput["name"]>(undefined),
    delete_old_positions: new FieldState<
      BulkEditTaskInput["delete_old_positions"]
    >(false),
    add_task_positions: new FormState<TaskPositionForm[]>([]),
  });

  constructor(
    private appStore: AppStore,
    private onSuccess?: () => void,
  ) {
    super([
      {
        id: "star",
        label: () => t`Star tasks`,
        icon: "star-01",
        callback: () => this.handleBulkStar(this.selectedIdsArray),
        isLoading: () => this.appStore.starStore.isBulkLoading,
      },
      {
        id: "unstar",
        label: () => t`Unstar tasks`,
        icon: "star-01",
        iconColorScheme: "red",
        callback: () => this.handleBulkUnstar(this.selectedIdsArray),
        isLoading: () => this.appStore.starStore.isBulkLoading,
      },
      {
        id: "edit",
        label: () => t`Edit tasks`,
        icon: "edit-02",
        callback: () => {
          this.handleModalOpen("edit");
        },
      },
      {
        id: "duplicate",
        label: () => t`Duplicate tasks`,
        icon: "copy-02",
        callback: () => {
          this.handleModalOpen("duplicate");
        },
      },
      {
        id: "move",
        label: () => t`Move tasks`,
        icon: "move",
        callback: () => {
          this.handleModalOpen("move");
        },
      },
      {
        id: "delete",
        label: () => t`Delete tasks`,
        icon: "trash-03",
        iconColorScheme: "red",
        callback: () => {
          this.handleModalOpen("delete");
        },
      },
    ]);
    makeObservable(this);
  }

  @computed get isModalOpen() {
    return this.actionType !== null;
  }

  @computed get isLoading() {
    return (
      this.editMutator.isLoading.value || this.deleteMutator.isLoading.value
    );
  }

  @action setActionType(actionType: ActionType) {
    this.actionType = actionType;
  }

  @action
  setOnSuccess(onSuccess: () => void) {
    this.onSuccess = onSuccess;
  }

  @action.bound handleModalClose() {
    this.setActionType(null);
    this.form.reset();
    this.userOptions = [];
    this.workTypeOptions = [];
    this.mentionedUserIds = [];
    this.mentionedTeamIds = [];
    this.mentionedAssignees = false;
    this.positionMappingStore.reset();
  }

  @action.bound async handleModalOpen(actionType: ActionType) {
    this.setActionType(actionType);
    this.fetchOptions();
    this.fetchUserOptions();
    this.resetPositions();
  }

  @action.bound async handleConfirm() {
    if (this.actionType === "delete") {
      this.handleDelete(this.selectedIdsArray);
      return;
    }

    if (
      this.positionMappingStore.hasUnmatchedPositions &&
      !this.positionMappingStore.allPositionsMapped
    ) {
      this.positionMappingStore.wrapperRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    const formRes = await this.form.validate();
    if (formRes.hasError) return;

    const descriptionValue =
      this.descriptionInputRef.current?.getContent() ?? EMPTY_EDITOR_CONTENT;

    this.handleEdit(
      this.selectedIdsArray,
      {
        delete_old_positions: formRes.value.delete_old_positions.$,
        add_task_positions:
          formRes.value.add_task_positions.$.length > 1 ||
          Boolean(
            formRes.value.add_task_positions.$[0].$.time_tracking_work_type_id
              .$,
          )
            ? formRes.value.add_task_positions.$.map((position) => ({
                user_id: position.$.user_id.$,
                planned_time:
                  parseFloat(position.$.planned_time.$) * ONE_HOUR_IN_SECONDS,
                time_tracking_work_type_id:
                  position.$.time_tracking_work_type_id.$,
              }))
            : undefined,
        billable: formRes.value.billable.$
          ? formRes.value.billable.$ === "true"
          : undefined,
        our_work_budget_item_id: formRes.value.our_work_budget_item_id.$,
        task_status_id: formRes.value.task_status_id.$,
        task_priority_id: formRes.value.task_priority_id.$,
        name: formRes.value.name.$,
        parent_task_id: formRes.value.parent_task_id.$,
        deadline: formRes.value.deadline.$
          ? toApiDate(formRes.value.deadline.$)
          : undefined,
        from: formRes.value.from.$
          ? toApiDate(formRes.value.from.$)
          : undefined,
        to: formRes.value.to.$ ? toApiDate(formRes.value.to.$) : undefined,
        description:
          descriptionValue.length > EMPTY_EDITOR_LENGTH
            ? {
                body: descriptionValue,
                mentioned_user_ids: this.mentionedUserIds,
                mentioned_team_ids: this.mentionedTeamIds,
                mentioned_assignees: this.mentionedAssignees,
              }
            : undefined,
        position_mapping: this.positionMappingStore.serializedMapping,
      },
      this.actionType === "duplicate",
    );
  }

  @action.bound
  handleChangeDescription(
    _: Content,
    {
      mentioned_user_ids,
      mentioned_team_ids,
      mentioned_assignees,
    }: {
      mentioned_user_ids: TaskDescriptionInput["mentioned_user_ids"];
      mentioned_team_ids: TaskDescriptionInput["mentioned_team_ids"];
      mentioned_assignees: TaskDescriptionInput["mentioned_assignees"];
    },
  ) {
    this.mentionedUserIds = mentioned_user_ids;
    this.mentionedTeamIds = mentioned_team_ids;
    this.mentionedAssignees = mentioned_assignees;
  }

  async handleEdit(
    taskIds: string[],
    input: BulkEditTaskInput,
    duplicateTasks: boolean,
  ) {
    const [, error] = await this.editMutator.mutate({
      taskIds,
      data: input,
      duplicateTasks,
    });

    if (error) return;

    this.handleModalClose();
    this.appStore.tasksStore.viewStore.resetPagination();
    this.onSuccess?.();
  }

  async handleDelete(taskIds: string[]) {
    const [, error] = await this.deleteMutator.mutate({ taskIds });

    if (error) return;

    this.appStore.UIStore.toast({
      title: t`Tasks deleted successfully`,
      status: "success",
    });

    this.selectedIds.forEach((id) => {
      this.appStore.tasksStore.viewStore.removeTask(id);
    });

    this.selectedIds = new Set();
    this.handleModalClose();
    this.onSuccess?.();
  }

  @computed get positionsTotalHours() {
    return this.form.$.add_task_positions.$.reduce((prev, { $ }) => {
      return prev + parseFloat($.planned_time.value);
    }, 0).toString();
  }

  @action.bound resetPositions() {
    this.form.$.add_task_positions = new FormState<TaskPositionForm[]>([]);
    this.addPosition();
  }

  private requiredIfMultiplePositionsOrPersonSelected = (
    $: string | undefined,
  ) => {
    if (this.form.$.add_task_positions.$.length > 1) return required($);
    if (
      this.form.$.add_task_positions.$.length === 1 &&
      Boolean(this.form.$.add_task_positions.$[0].$.user_id.value)
    )
      return required($);
    return undefined;
  };

  @action.bound addPosition() {
    this.form.$.add_task_positions.$.push(
      new FormState({
        user_id: new FieldState(""),
        planned_time: new FieldState("0"),
        time_tracking_work_type_id: new FieldState("").validators(
          this.requiredIfMultiplePositionsOrPersonSelected,
        ),
      }),
    );
  }

  @action.bound removePosition(index: number) {
    if (this.form.$.add_task_positions.$.length === 1) {
      this.resetPositions();
      return;
    }
    this.form.$.add_task_positions.$.splice(index, 1);
  }

  @computed get isFetchingOptions() {
    return this.optionsFetcher.isLoading.value;
  }

  async fetchOptions() {
    const [data, error] = await this.optionsFetcher.fetch();

    if (error) return;

    this.taskStatusOptions = data.taskStatuses.map((status) => ({
      label: status.name,
      value: status.id,
    }));
  }

  @computed get isFetchingUserOptions() {
    return this.usersFetcher.isLoading.value;
  }

  async fetchUserOptions() {
    const [data, error] = await this.usersFetcher.fetch({
      projectId: this.form.$.project_id.value,
    });

    if (error) return;

    this.userOptions = mapToOptions.users(data.userSimpleMap);
  }

  async fetchPrepareTaskData(destinationBudgetItemId: string | undefined) {
    if (!destinationBudgetItemId) {
      this.workTypeOptions = [];
      this.positionMappingStore.reset();
      return;
    }

    const [data, error] = await this.prepareTaskDataFetcher.fetch({
      taskIds: this.selectedIdsArray,
      destinationBudgetItemId,
    });

    if (error) {
      this.workTypeOptions = [];
      return;
    }

    this.workTypeOptions = mapToOptions.workTypes(
      data.prepareTaskDataForBulkActions.available_positions,
    );
    this.unavailableWorkTypes =
      data.prepareTaskDataForBulkActions.unavailable_positions;

    if (this.form.$.project_id.value) {
      await this.positionMappingStore.fetchUnmatched(
        this.selectedIdsArray,
        this.form.$.project_id.value,
      );
    }
  }

  @action.bound handleChangeProjectId(value: string) {
    this.form.$.project_id.onChange(value);
    this.form.$.our_work_budget_item_id.onChange(undefined);
    this.resetPositions();
    this.positionMappingStore.reset();
  }

  @action.bound handleChangeBudgetItemId(value: string) {
    this.form.$.our_work_budget_item_id.onChange(value);
    this.fetchPrepareTaskData(value);
    this.resetPositions();
  }

  @action.bound
  async handleBulkStar(taskIds: string[]) {
    const [data, error] = await this.appStore.starStore.bulkStar(
      taskIds,
      StarsStarableTypeEnum.Task,
    );

    if (error || !data?.bulkStar) return;

    this.updateStarredTasks(taskIds, true);

    this.appStore.UIStore.toast({
      title: t`Starred ${data.bulkStar.total - data.bulkStar.failed} out of ${data.bulkStar.total} tasks`,
      status: "success",
    });

    this.handleCancelSelection();
    this.onSuccess?.();
  }

  @action.bound
  async handleBulkUnstar(taskIds: string[]) {
    const [data, error] = await this.appStore.starStore.bulkUnstar(
      taskIds,
      StarsStarableTypeEnum.Task,
    );

    if (error || !data?.bulkUnstar) return;

    this.updateStarredTasks(taskIds, false);

    this.appStore.UIStore.toast({
      title: t`Unstarred ${data.bulkUnstar.total - data.bulkUnstar.failed} out of ${data.bulkUnstar.total} tasks`,
      status: "success",
    });

    this.handleCancelSelection();
    this.onSuccess?.();
  }

  @action.bound
  private updateStarredTasks(ids: string[], isStarred: boolean) {
    ids.forEach((id) => {
      this.appStore.tasksStore.viewStore.updateTask({
        id,
        is_starred: isStarred,
      });
    });
  }
}
